import {
  useController,
  type Control,
  type FieldValues,
  type Path,
  type FieldError,
  type RegisterOptions,
} from 'react-hook-form';
import { Input, type InputProps } from '@/components/ui/Input';
import { Select, type SelectOption } from '@/components/ui/Select';

export interface FormFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  hint?: string;
  required?: boolean;
  /** react-hook-form validation rules (e.g. a `pattern` for regex formats). */
  rules?: RegisterOptions<T, Path<T>>;
}

export interface FormInputProps<T extends FieldValues>
  extends FormFieldProps<T>, Omit<InputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'ref'> {}

export function FormInput<T extends FieldValues>({
  name,
  control,
  label,
  hint,
  required,
  rules,
  ...inputProps
}: FormInputProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules });

  return (
    <Input
      {...inputProps}
      {...field}
      // Ensure the input is always controlled — RHF may yield undefined for
      // fields absent from defaultValues, which flips the input from
      // uncontrolled to controlled and triggers a React warning.
      value={field.value ?? ''}
      label={label}
      hint={hint}
      error={getErrorMessage(error)}
      required={required}
    />
  );
}

export interface FormSelectProps<T extends FieldValues> extends FormFieldProps<T> {
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function FormSelect<T extends FieldValues>({
  name,
  control,
  label,
  hint,
  required,
  options,
  placeholder,
  disabled,
  className,
}: FormSelectProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  return (
    <Select
      {...field}
      label={label}
      hint={hint}
      error={getErrorMessage(error)}
      required={required}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}

export interface FormTextareaProps<T extends FieldValues> extends FormFieldProps<T> {
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  maxLength?: number;
  className?: string;
}

export function FormTextarea<T extends FieldValues>({
  name,
  control,
  label,
  hint,
  required,
  placeholder,
  rows = 3,
  disabled,
  maxLength,
  className,
}: FormTextareaProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  const errorMessage = getErrorMessage(error);
  const fieldId = label ? label.toLowerCase().replace(/\s+/g, '-') : name;
  const errorId = errorMessage ? `${fieldId}-error` : undefined;
  const hintId = hint && !errorMessage ? `${fieldId}-hint` : undefined;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={fieldId}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && (
            <span className="ml-0.5 text-danger-500" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      <textarea
        {...field}
        id={fieldId}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        maxLength={maxLength}
        className={`block w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-surface-secondary disabled:opacity-60 dark:bg-surface-dark-secondary dark:text-gray-100 dark:placeholder:text-gray-500 ${
          errorMessage
            ? 'border-danger-500 focus:ring-danger-500'
            : 'border-border focus:border-brand-500 focus:ring-brand-500 dark:border-border-dark'
        } ${className ?? ''}`}
        aria-invalid={errorMessage ? 'true' : undefined}
        aria-describedby={errorId || hintId}
      />
      {errorMessage && (
        <p id={errorId} className="mt-1.5 text-sm text-danger-500" role="alert">
          {errorMessage}
        </p>
      )}
      {hint && !errorMessage && (
        <p id={hintId} className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}
    </div>
  );
}

export interface FormCheckboxProps<T extends FieldValues> extends FormFieldProps<T> {
  description?: string;
  disabled?: boolean;
}

export function FormCheckbox<T extends FieldValues>({
  name,
  control,
  label,
  description,
  disabled,
}: FormCheckboxProps<T>) {
  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController({ name, control });

  return (
    <div className="flex items-start gap-3">
      <input
        {...field}
        type="checkbox"
        checked={Boolean(value)}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      <div>
        {label && (
          <label
            htmlFor={field.name}
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {label}
          </label>
        )}
        {description && <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>}
        {error && (
          <p id={`${name}-error`} className="mt-1 text-sm text-danger-500" role="alert">
            {getErrorMessage(error)}
          </p>
        )}
      </div>
    </div>
  );
}

function getErrorMessage(error: FieldError | undefined): string | undefined {
  return error?.message;
}
